import { complement, isNil, difference, pick, omit } from "ramda";
import { v4 as uuidv4 } from "uuid";

import {
  addRequestTypes,
  requestTypes,
  responseTypes,
  requestOriginTypes,
} from "../enums/requestTypes";
import { getStopParcelCodes, findKingParcel } from "./stop";
import { isDeferredRequest } from "../features/routeDetail/model";

export const generateRequest = ({
  requestType,
  parcel = {},
  stop = {},
  routeId = null,
  requestOrder,
}) => ({
  routeId,
  requestType,
  requestId: uuidv4(),
  labelNumber: parcel.labelNumber || null,
  parcelCode: parcel.parcelCode || null,
  stopNumber: stop.stopNumber || null,
  collectionCode: stop.collection?.collectionCode || null,
  requestOrigin: requestOriginTypes.ROUTE_PLANNER,
  order: requestOrder,
  sourceStopId: stop.stopId || null,
});

export const setResponse = ({
  responseType,
  originalStopNumber = null,
  newStopNumber = null,
  originalRouteId = null,
  newRouteId = null,
}) => (request) => ({
  ...request,
  response: {
    responseType,
    originalStopNumber,
    newStopNumber,
    originalRouteId,
    newRouteId,
  },
});

export const resetResponse = (request) => omit(["response"], request);

export const isRequestFromRoutePlanner = ({ requestOrigin }) =>
  requestOrigin === requestOriginTypes.ROUTE_PLANNER;

export const isRequestFromSaturn = ({ requestOrigin }) =>
  requestOrigin === requestOriginTypes.SATURN;

export const getRequestsParcelCodes = (requests = []) =>
  requests.reduce(
    (acc, item) =>
      !!item && item.parcelCode ? [...acc, item.parcelCode] : acc,
    []
  );

export const getRequestsByParcelCodes = (requests, parcelCodes, requestType) =>
  requests.filter((r) =>
    !r.response || r.response.responseType === responseTypes.DEFER
      ? parcelCodes.find((parcelCode) =>
          requestType
            ? r.parcelCode === parcelCode && r.requestOrigin === requestType
            : r.parcelCode === parcelCode
        )
      : false
  );

export const getDeferredRequests = (requests = []) =>
  requests.filter(
    (request) =>
      request.response && request.response.responseType === responseTypes.DEFER
  );

export const setDeferRequest = setResponse({
  responseType: responseTypes.DEFER,
});

export const setRejectRequest = (request) =>
  setResponse({
    responseType: responseTypes.REJECT,
    newRouteId: isRequestFromRoutePlanner(request) ? "000" : null,
  })(request);

export const generateTransferResponse = (
  routeId,
  selectedRouteId,
  stopNumber,
  responseType
) => {
  return setResponse({
    responseType,
    originalStopNumber: stopNumber,
    originalRouteId: routeId,
    newRouteId: selectedRouteId,
  });
};

export const isPlannedAddRequest = (request) =>
  addRequestTypes.includes(request.requestType) &&
  !isRequestFromRoutePlanner(request) &&
  request.plannedRoute;

export const isNotPlannedAddRequest = (request) =>
  addRequestTypes.includes(request.requestType) &&
  !isRequestFromRoutePlanner(request) &&
  isNil(request.plannedRoute);

export const isShopUnsuitableRequest = (request) =>
  request.requestType === requestTypes.AMEND_PICKUP_PARCELS &&
  !isNil(request.address);

export const getPlannedRequestsForReject = (requests) =>
  requests.filter(complement(isNotPlannedAddRequest));

export const generateMoveParcelRequests = (
  parcels,
  fromStop,
  toStop,
  routeId,
  requestOrder
) => {
  const newRequests = [];

  (parcels || []).forEach((parcel) => {
    newRequests.push(
      ...[requestTypes.REMOVE_PARCEL, requestTypes.ADD_PARCEL].map(
        (requestType) =>
          setResponse({
            responseType: responseTypes.ACCEPT,
            originalStopNumber: fromStop.stopNumber,
            newStopNumber: toStop.stopNumber,
            originalRouteId: routeId,
            newRouteId: routeId,
          })(
            generateRequest({
              requestType,
              parcel,
              stop: requestType === requestTypes.ADD_PARCEL ? toStop : fromStop,
              requestOrder,
            })
          )
      )
    );
  });
  return newRequests;
};

export const updateRouteRequestsWithMapper = (
  routeRequests,
  requests,
  mapper
) => {
  const updateRequestsMap = requests.reduce((acc, item) => {
    acc[item.requestId] = item;
    return acc;
  }, {});
  return routeRequests.map((request) =>
    updateRequestsMap[request.requestId] ? mapper(request) : request
  );
};

const generateRequestToAddCollectionToTransferRoute = (
  transferRouteId,
  stop,
  requestOrder
) => ({
  ...generateRequest({
    requestType: requestTypes.ADD_COLLECTION,
    stop,
    routeId: transferRouteId,
    requestOrder,
  }),
  serviceStartTime: stop.serviceStartTime,
  serviceEndTime: stop.serviceEndTime,
});

export const generateCollectionStopTransferRequests = (
  routeId,
  transferRouteId,
  stop,
  requestOrder
) => {
  return [
    setResponse(
      generateTransferResponse(
        routeId,
        transferRouteId,
        stop.stopNumber,
        responseTypes.ACCEPT
      )().response
    )(
      generateRequest({
        requestType: requestTypes.REMOVE_COLLECTION,
        stop,
        requestOrder,
      })
    ),
    generateRequestToAddCollectionToTransferRoute(
      transferRouteId,
      stop,
      requestOrder
    ),
  ];
};

export const generateTransferRequestToRemoveParcelFromStop = (
  parcel,
  stop,
  routeId,
  transferRouteId,
  requestOrder
) =>
  setResponse({
    responseType: responseTypes.ACCEPT,
    originalStopNumber: stop.stopNumber,
    newStopNumber: null,
    originalRouteId: routeId,
    newRouteId: transferRouteId,
  })(
    generateRequest({
      requestType: requestTypes.REMOVE_PARCEL,
      parcel,
      stop,
      requestOrder,
    })
  );

export const generateRequestToAddParcelToTransferRoute = (
  parcels,
  transferRouteId,
  requestOrder,
  stop
) => {
  const leadParcel = findKingParcel(parcels) || parcels[0];
  const relatedStopParcels = parcels
    .filter((parcel) => parcel.parcelCode !== leadParcel.parcelCode)
    .map((parcel) =>
      pick(
        ["parcelCode", "labelNumber", "serviceEndTime", "serviceStartTime"],
        parcel
      )
    );
  return {
    ...generateRequest({
      requestType: requestTypes.ADD_PARCEL,
      parcel: leadParcel,
      requestOrder,
    }),
    sourceStopId: stop.stopId || null,
    routeId: transferRouteId,
    serviceStartTime: leadParcel?.serviceStartTime,
    serviceEndTime: leadParcel?.serviceEndTime,
    relatedStopParcels,
  };
};

export const generateMergeToStopRequests = (
  requests,
  routeId,
  requestOrder
) => {
  return requests.map((request) => {
    const newRequestType =
      request.requestType === requestTypes.REMOVE_COLLECTION
        ? requestTypes.ADD_COLLECTION
        : requestTypes.ADD_PARCEL;

    return generateRequest({
      requestType: newRequestType,
      parcel: request,
      stop: { collection: { collectionCode: request.collectionCode } },
      routeId,
      requestOrder,
    });
  });
};

export const processParcelTransferToRequests = (
  currentRoute,
  transferRouteId,
  stop,
  parcels,
  requestOrder
) => {
  const transferParcels = parcels || stop.parcel;
  const isFullTransfer = transferParcels.length === stop.parcel.length;
  const processedStop = isFullTransfer ? stop : omit(["stopId"], stop);
  let requests = [];
  transferParcels.forEach((parcel) => {
    requests.push(
      generateTransferRequestToRemoveParcelFromStop(
        parcel,
        processedStop,
        currentRoute.routeId,
        transferRouteId,
        requestOrder
      )
    );
  });
  requests.push(
    generateRequestToAddParcelToTransferRoute(
      transferParcels,
      transferRouteId,
      requestOrder,
      processedStop
    )
  );
  return requests;
};

export const getValidateParcelList = (stop, requests) =>
  difference(
    getStopParcelCodes(stop),
    requests.map(({ parcelCode }) => parcelCode)
  );

export const isAddParcelRequest = (request) =>
  request?.requestType === requestTypes.ADD_PARCEL;

export const isAddCollectionRequest = (request) =>
  request?.requestType === requestTypes.ADD_COLLECTION;

export const isAddRequest = (request) =>
  isAddParcelRequest(request) || isAddCollectionRequest(request);

export const filterPendingRequests = (stopParcelCodes, requests) =>
  stopParcelCodes.filter((parcelCode) => {
    const parcelRequest = requests.find(
      (r) =>
        r.parcelCode === parcelCode &&
        ((r.originType === requestOriginTypes.SATURN && isDeferredRequest(r)) ||
          (isAddParcelRequest(r) && (!r?.response || isDeferredRequest(r))))
    );

    return !parcelRequest;
  });

export const getUpdatedParcels = (parcels, parcelsFromAPI) => {
  const parcelCodesFromAPI = new Set(
    parcelsFromAPI.map(({ parcelCode }) => parcelCode)
  );
  return [
    ...parcels.filter(({ parcelCode }) => !parcelCodesFromAPI.has(parcelCode)),
    ...parcelsFromAPI,
  ];
};
